import { useMemo } from 'react';
import CustomSelect from '../../components/CustomSelect';
import CustomDateRangePicker from '../../components/CustomDateRangePicker';
import { useFilters } from '../../hooks/useFilters';
import { useGetProjectsQuery } from '../../services/statApi';

const INTERVAL_OPTIONS = [
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'month', label: 'Month' },
];

export default function Filters({ stat = [] }) {
  const [filters, setFilters] = useFilters();
  const { data: projects } = useGetProjectsQuery();

  const projectOptions = useMemo(
    () => [
      { value: '', label: 'All Projects' },
      ...(projects ?? []).map((p) => ({ value: p, label: p })),
    ],
    [projects],
  );

  // The API exposes no campaign endpoint, so the option list is derived from
  // the rows currently loaded for the selected project and period.
  const campaignOptions = useMemo(() => {
    const ids = new Set();
    for (const row of stat) {
      if (row.campaignid != null && row.campaignid !== '') ids.add(String(row.campaignid));
    }
    // Keep the active campaign selectable even when the current response no
    // longer contains it.
    if (filters.campaignId) ids.add(filters.campaignId);
    return [
      { value: '', label: 'All Campaigns' },
      ...[...ids]
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((id) => ({ value: id, label: id })),
    ];
  }, [stat, filters.campaignId]);

  // Presets are persisted by key so "Last 7 Days" keeps rolling; hand-picked
  // dates are persisted as absolute values.
  const handleDateRangeChange = (start, end, presetKey) => {
    setFilters(presetKey ? { dateRange: presetKey } : { dateRange: '', start, end });
  };

  return (
    <div className="grid w-full min-w-0 grid-cols-2 items-end gap-2 md:grid-cols-[repeat(auto-fit,minmax(160px,1fr))] md:gap-3">
      <CustomSelect
        label="Project"
        value={filters.project ?? ''}
        onChange={(event) => setFilters({ project: event.target.value, campaignId: '' })}
        options={projectOptions}
        placeholder="Select Project"
        searchable={true}
      />

      <CustomSelect
        label="Campaign"
        value={filters.campaignId}
        onChange={(event) => setFilters({ campaignId: event.target.value })}
        options={campaignOptions}
        placeholder="All Campaigns"
        searchable={true}
      />

      <CustomSelect
        label="Interval"
        value={filters.interval}
        onChange={(event) => setFilters({ interval: event.target.value })}
        options={INTERVAL_OPTIONS}
        placeholder="Select Interval"
      />

      <label className="flex h-9 min-w-0 cursor-pointer touch-manipulation items-center self-end rounded-sm border border-line bg-surface-elevated px-2 text-[0.8rem] text-fg transition-colors select-none hover:border-accent has-focus-visible:border-accent md:px-3 md:text-[0.9rem]">
        <input
          type="checkbox"
          checked={filters.excludeNoInstalls}
          onChange={(e) => setFilters({ excludeNoInstalls: e.target.checked })}
          className="mr-2 size-4 shrink-0 cursor-pointer accent-accent"
        />
        <span className="truncate">
          <span className="md:hidden">Exclude empty</span>
          <span className="max-md:hidden">Exclude no installs</span>
        </span>
      </label>

      <div className="col-span-2 md:col-span-1">
        <span className="mb-1 block text-[0.8rem] font-medium text-fg-muted">Date Range</span>
        <CustomDateRangePicker
          startDate={filters.start}
          endDate={filters.end}
          onChange={handleDateRangeChange}
        />
      </div>
    </div>
  );
}
